import {
  Auth0AccountRole,
  BuiltinField,
  Condition,
  ConditionGroup,
  ComparisonOperator,
  OrganizationDataKeyResponse,
  DataType,
} from "client/openapi";

const EQUALITY = [ComparisonOperator.EQUALS, ComparisonOperator.NOT_EQUALS];
const ORDER = [
  ComparisonOperator.GREATER_THAN,
  ComparisonOperator.GREATER_THAN_OR_EQUAL,
  ComparisonOperator.LESS_THAN,
  ComparisonOperator.LESS_THAN_OR_EQUAL,
];
const LIST = [ComparisonOperator.IN, ComparisonOperator.NOT_IN];

/**
 * The ranges a date can be "within", counted from today in the
 * organization's time zone each time the rule runs. A rule stores the
 * range, never dates, so it stays current.
 */
export const RELATIVE_DATE_RANGES = [
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "last_90_days", label: "Last 90 days" },
  { value: "last_year", label: "Last year" },
  { value: "next_7_days", label: "Next 7 days" },
  { value: "next_30_days", label: "Next 30 days" },
  { value: "next_90_days", label: "Next 90 days" },
  { value: "next_year", label: "Next year" },
];

/** Whether a value is one of the relative ranges. */
export function isRelativeRange(value: unknown): boolean {
  return RELATIVE_DATE_RANGES.some((range) => range.value === value);
}

/**
 * The operators each kind of field can run. The editor offers only these,
 * and a rule using any other is not valid, so a saved rule always runs.
 * Lists are picked from options, so only list fields take "in".
 */
export const OPERATORS_BY_TYPE: Record<DataType, ComparisonOperator[]> = {
  [DataType.TEXT]: [...EQUALITY, ComparisonOperator.ILIKE],
  [DataType.FLOAT]: [...EQUALITY, ...ORDER],
  [DataType.DATE]: [...EQUALITY, ...ORDER, ComparisonOperator.WITHIN],
  [DataType.BOOLEAN]: EQUALITY,
  [DataType.CUSTOM_DATA_TYPE]: [...EQUALITY, ...LIST],
};

/** Built-in fields (Subjects, Payment method) are compared as sets. */
export const BUILTIN_OPERATORS = [...EQUALITY, ...LIST];

const { ME, PARENT, ORG_TUTOR } = Auth0AccountRole;

export type BuiltinFieldInfo = {
  field: BuiltinField;
  label: string;
  people: Auth0AccountRole[];
  type: "SUBJECTS" | "PAYMENT_METHOD" | DataType;
  form?: true;
};

/**
 * Tadpole's built-in fields, in the order they're listed: their names, who
 * has them, and what they're compared as. Subjects and Payment method are
 * compared as sets; the others are worked out from the app's records and
 * compared as a field of their type is. A form field names its form first.
 */
export const BUILTIN_FIELDS: BuiltinFieldInfo[] = [
  {
    field: BuiltinField.SUBJECTS,
    label: "Subjects",
    people: [ME, ORG_TUTOR],
    type: "SUBJECTS",
  },
  {
    field: BuiltinField.PAYMENT_METHOD,
    label: "Payment Method",
    people: [ME, PARENT],
    type: "PAYMENT_METHOD",
  },
  {
    field: BuiltinField.LAST_MEETING_DATE,
    label: "Date of Last Meeting",
    people: [ME, ORG_TUTOR],
    type: DataType.DATE,
  },
  {
    field: BuiltinField.NEXT_MEETING_DATE,
    label: "Date of Next Meeting",
    people: [ME, ORG_TUTOR],
    type: DataType.DATE,
  },
  {
    field: BuiltinField.BALANCE_OWED,
    label: "Unpaid Tutoring",
    people: [ME, PARENT],
    type: DataType.FLOAT,
  },
  {
    field: BuiltinField.CREDIT_BALANCE,
    label: "Prepaid Tutoring",
    people: [ME, PARENT],
    type: DataType.FLOAT,
  },
  {
    field: BuiltinField.LAST_CONTACTED,
    label: "Last Contacted",
    people: [ME, PARENT, ORG_TUTOR],
    type: DataType.DATE,
  },
  {
    field: BuiltinField.MEETINGS_LAST_30_DAYS,
    label: "Meetings in the Last 30 Days",
    people: [ME, ORG_TUTOR],
    type: DataType.FLOAT,
  },
  {
    field: BuiltinField.FORM_FILLED_OUT,
    label: "Form Filled Out",
    people: [ME, PARENT, ORG_TUTOR],
    type: DataType.BOOLEAN,
    form: true,
  },
  {
    field: BuiltinField.FORM_FILLED_OUT_DATE,
    label: "Date Form Filled Out",
    people: [ME, PARENT, ORG_TUTOR],
    type: DataType.DATE,
    form: true,
  },
];

/** A built-in field's entry in BUILTIN_FIELDS. */
export function builtinFieldInfo(field: BuiltinField | null | undefined) {
  return BUILTIN_FIELDS.find((info) => info.field === field);
}

/** The operators a built-in field can run. */
export function builtinOperators(info: BuiltinFieldInfo): ComparisonOperator[] {
  return info.type === "SUBJECTS" || info.type === "PAYMENT_METHOD"
    ? BUILTIN_OPERATORS
    : OPERATORS_BY_TYPE[info.type];
}

/** Whether an operator takes a list of values rather than one. */
export function takesList(operator: ComparisonOperator): boolean {
  return LIST.includes(operator);
}

/** Validates a condition to ensure it has valid values */
export function validateCondition(
  condition: Condition,
  availableKeys: OrganizationDataKeyResponse[]
): boolean {
  const { comparison_operator: operator, value } = condition;
  const builtin = builtinFieldInfo(condition.builtin_field);
  const key = builtin
    ? undefined
    : availableKeys.find((k) => k.id === condition.org_data_key_id);
  if (!builtin && (!condition.org_data_key_id || !key)) return false;
  // A form field needs its form.
  if (builtin?.form && !condition.form_id) return false;

  const allowed = builtin
    ? builtinOperators(builtin)
    : key
    ? OPERATORS_BY_TYPE[key.data_type]
    : [];
  if (!allowed?.includes(operator)) return false;

  // "within" takes a range; a date's other operators take a date.
  if (operator === ComparisonOperator.WITHIN) return isRelativeRange(value);

  // A list operator takes a non-empty list; any other takes one value.
  if (takesList(operator)) {
    return Array.isArray(value) && value.length > 0;
  }
  if (Array.isArray(value)) return false;

  if (condition.builtin_field === BuiltinField.PAYMENT_METHOD) {
    return typeof value === "string" && value !== "";
  }
  if (condition.builtin_field === BuiltinField.SUBJECTS) {
    return typeof value === "number";
  }

  // For non-list operators, check based on data type (a built-in field's
  // as a field of its type)
  switch (builtin ? builtin.type : key?.data_type) {
    case DataType.TEXT:
      return (
        condition.value !== null &&
        condition.value !== undefined &&
        condition.value !== ""
      );
    case DataType.FLOAT:
      return (
        condition.value !== null &&
        condition.value !== undefined &&
        condition.value !== "" &&
        !isNaN(Number(condition.value))
      );
    case DataType.DATE:
      return (
        condition.value !== null &&
        condition.value !== undefined &&
        condition.value !== "" &&
        !isRelativeRange(condition.value)
      );
    case DataType.BOOLEAN:
      return (
        condition.value !== null &&
        condition.value !== undefined &&
        condition.value !== ""
      );
    case DataType.CUSTOM_DATA_TYPE:
      return (
        condition.value !== null &&
        condition.value !== undefined &&
        condition.value !== ""
      );
    default:
      return true; // TEXT and other types allow empty strings
  }
}

/** Validates a condition group recursively */
export function validateConditionGroup(
  group: ConditionGroup,
  availableKeys: OrganizationDataKeyResponse[]
): boolean {
  return group.conditions.every((condition) => {
    if ("conditions" in condition) {
      // This is a nested group
      return validateConditionGroup(condition as ConditionGroup, availableKeys);
    } else {
      // This is a single condition
      return validateCondition(condition, availableKeys);
    }
  });
}

/** Checks if a query is valid for a single entity type */
export function isSingleEntityQueryValid(
  group: ConditionGroup,
  availableKeys: OrganizationDataKeyResponse[]
): boolean {
  return validateConditionGroup(group, availableKeys);
}

/** Whether nothing is picked in a group: each of its rows has no field yet. */
export function isBlankGroup(group: ConditionGroup): boolean {
  return group.conditions.every((item) =>
    "conditions" in item
      ? isBlankGroup(item as ConditionGroup)
      : !item.org_data_key_id && !item.builtin_field
  );
}

/**
 * A side of a combined search as it is sent: a blank side has no
 * conditions, which the server reads as no filter on that side.
 */
export function withoutBlankSide(group: ConditionGroup): ConditionGroup {
  return isBlankGroup(group) ? { ...group, conditions: [] } : group;
}

/**
 * Checks if a combined query is valid: either side may be left blank, not
 * both, and a side with anything picked must be complete.
 */
export function isMultiEntityQueryValid(
  groupA: ConditionGroup,
  groupB: ConditionGroup,
  keysA: OrganizationDataKeyResponse[],
  keysB: OrganizationDataKeyResponse[]
): boolean {
  const blankA = isBlankGroup(groupA);
  const blankB = isBlankGroup(groupB);
  if (blankA && blankB) return false;
  return (
    (blankA || validateConditionGroup(groupA, keysA)) &&
    (blankB || validateConditionGroup(groupB, keysB))
  );
}
